import { useEffect, useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { fmtMoney } from "@/lib/format";
import { errorMessage } from "@/lib/errors";
import { api } from "@/lib/api";
import { Search, Target, TrendingUp, TrendingDown, Clock, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const fetchSourcing = (q) =>
  api.get("/sourcing/lookup", { params: q ? { q } : {} }).then((r) => r.data);

function tone(roiPct) {
  if (roiPct === null || roiPct === undefined) return "text-[#3A2A10]";
  if (roiPct >= 100) return "text-emerald-600";
  if (roiPct >= 25) return "text-emerald-700/80";
  if (roiPct >= 0) return "text-[#0A0604]";
  return "text-rose-600";
}

function MetricRow({ label, value, sub, mono = true, hint, tone: t }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1.5 border-b border-[#8B6A1F]/15 last:border-0">
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#3A2A10]" title={hint}>
        {label}
      </div>
      <div className="text-right">
        <div className={`${mono ? "font-mono-data" : ""} text-sm ${t || "text-[#0A0604]"}`}>{value}</div>
        {sub && <div className="text-[10px] text-[#3A2A10] font-mono-data mt-0.5">{sub}</div>}
      </div>
    </div>
  );
}

function ResultCard({ r, feePct, markupPct }) {
  const thin = r.n < 5;
  return (
    <div
      className="bg-[#F9F0D4] border border-[#8B6A1F]/35 rounded-sm p-5 flex flex-col gap-1"
      data-testid={`sourcing-card-${r.brand}-${r.caliber}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#3A2A10] mb-1">
            {r.brand || "—"}
          </div>
          <div className="font-serif-display text-2xl text-[#0A0604] leading-tight">
            {r.caliber || "—"}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#3A2A10]">n=</div>
          <div className={`font-mono-data text-2xl ${thin ? "text-amber-600" : "text-[#0A0604]"}`}>
            {r.n}
          </div>
          {thin && (
            <div className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider text-amber-700 mt-1" title="Small sample — averages are noisy.">
              <AlertTriangle size={10} /> thin
            </div>
          )}
        </div>
      </div>

      {/* Suggested max bid — the signature insight */}
      <div className="flex items-center justify-between gap-3 px-4 py-3 mb-3 bg-[#D4AF37]/15 border border-[#D4AF37]/45 rounded-sm">
        <div className="flex items-center gap-2 min-w-0">
          <Target size={16} className="text-[#0A0604] shrink-0" />
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#3A2A10]">Suggested Max Bid</div>
            <div className="text-[9px] text-[#3A2A10] mt-0.5 truncate">
              to hit {markupPct}% margin after {feePct}% fees
            </div>
          </div>
        </div>
        <div className="font-mono-data text-2xl text-[#0A0604] whitespace-nowrap">
          {r.suggested_max_bid !== null ? fmtMoney(r.suggested_max_bid) : "—"}
        </div>
      </div>

      <MetricRow
        label="Usual Acquisition"
        value={fmtMoney(r.avg_purchase)}
        sub={r.min_purchase !== null && r.max_purchase !== null ? `${fmtMoney(r.min_purchase)} – ${fmtMoney(r.max_purchase)}` : null}
        hint="Average purchase price across your sold watches in this group."
      />
      <MetricRow
        label="Restoration"
        value={r.avg_parts > 0 ? fmtMoney(r.avg_parts) : "$0.00"}
        sub={r.avg_parts === 0 ? "no parts logged" : "avg parts spend"}
        hint="Average parts ledger total per sold watch in this group."
      />
      <MetricRow
        label="Total Cost Basis"
        value={fmtMoney(r.avg_total)}
        hint="all_in + parts averaged across the group."
      />
      <MetricRow
        label="Usual Sale"
        value={fmtMoney(r.avg_sale)}
        sub={r.min_sale !== null && r.max_sale !== null ? `${fmtMoney(r.min_sale)} – ${fmtMoney(r.max_sale)}` : null}
        hint="Average sale price."
      />
      <MetricRow
        label="Avg Profit"
        value={fmtMoney(r.avg_profit)}
        tone={tone(r.roi_pct)}
        sub={r.roi_pct !== null ? `${r.roi_pct >= 0 ? "+" : ""}${r.roi_pct.toFixed(1)}% ROI` : null}
        hint="Average P/L per piece sold."
      />
      <MetricRow
        label="Time to Sell"
        value={r.avg_days_to_sell !== null ? `${r.avg_days_to_sell} days` : "—"}
        hint="Average days from purchase date to sale date."
      />
    </div>
  );
}

export default function Sourcing() {
  const [q, setQ] = useState("");
  const [qLive, setQLive] = useState("");
  const [data, setData] = useState({ results: [], fee_pct: 23, markup_pct: 25 });
  const [loading, setLoading] = useState(true);

  // debounced search
  useEffect(() => {
    const t = setTimeout(() => setQ(qLive.trim()), 300);
    return () => clearTimeout(t);
  }, [qLive]);

  useEffect(() => {
    setLoading(true);
    fetchSourcing(q)
      .then(setData)
      .catch((err) => toast.error(errorMessage(err, "Lookup failed")))
      .finally(() => setLoading(false));
  }, [q]);

  const summary = useMemo(() => {
    const rs = data.results || [];
    if (rs.length === 0) return null;
    const totalN = rs.reduce((s, r) => s + (r.n || 0), 0);
    const weightedRoi = rs.reduce((s, r) => s + (r.roi_pct || 0) * (r.n || 0), 0) / (totalN || 1);
    return { totalN, groups: rs.length, weightedRoi };
  }, [data.results]);

  return (
    <div className="px-6 lg:px-10 py-8 max-w-[1400px] pb-24" data-testid="sourcing-page">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#3A2A10] mb-1.5">Decisions</div>
        <h1 className="font-serif-display text-4xl text-[#0A0604] leading-none">Sourcing</h1>
        <p className="text-xs text-[#3A2A10] mt-3 max-w-2xl">
          About to bid on a watch? Type the brand and/or caliber. We'll show you what you've
          historically paid, restored, and sold for in that group — plus a suggested ceiling
          that protects your margin after fees.
        </p>
      </div>

      <div className="relative mb-6 max-w-xl">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#3A2A10]" />
        <Input
          value={qLive}
          onChange={(e) => setQLive(e.target.value)}
          placeholder="Type a brand, caliber, or both — e.g. 'Bulova 10BPAC'"
          className="pl-10 h-12 text-base bg-[#F9F0D4] border-[#8B6A1F]/45 rounded-sm focus-visible:ring-[#D4AF37]/40"
          data-testid="sourcing-search"
          autoFocus
        />
      </div>

      {/* Tiny summary chip */}
      {summary && !loading && (
        <div className="flex flex-wrap items-center gap-3 mb-5 text-[11px] text-[#3A2A10] font-mono-data">
          <span>
            {summary.groups} group{summary.groups === 1 ? "" : "s"} · {summary.totalN} sold watch{summary.totalN === 1 ? "" : "es"}
          </span>
          <span className="inline-flex items-center gap-1">
            {summary.weightedRoi >= 0 ? (
              <TrendingUp size={12} className="text-emerald-700" />
            ) : (
              <TrendingDown size={12} className="text-rose-600" />
            )}
            <span className={summary.weightedRoi >= 0 ? "text-emerald-700" : "text-rose-600"}>
              {summary.weightedRoi >= 0 ? "+" : ""}{summary.weightedRoi.toFixed(1)}% weighted ROI
            </span>
          </span>
          <span className="inline-flex items-center gap-1 text-[#3A2A10]">
            <Clock size={12} />
            {q ? `Showing matches for "${q}"` : "Top sellers across the catalogue"}
          </span>
        </div>
      )}

      {loading ? (
        <div className="text-[#3A2A10] text-sm py-12 text-center" data-testid="sourcing-loading">
          Crunching numbers…
        </div>
      ) : data.results?.length === 0 ? (
        <div className="border border-dashed border-[#8B6A1F]/45 bg-[#F9F0D4]/60 rounded-sm py-16 px-6 text-center" data-testid="sourcing-empty">
          <div className="font-serif-display text-2xl text-[#3A2A10] mb-2">No sold watches matched</div>
          <div className="text-xs text-[#3A2A10] max-w-md mx-auto">
            {q
              ? `Nothing in your sales history matches "${q}". Try a broader brand name or caliber prefix.`
              : "Once you mark a few watches SOLD, they'll start populating sourcing intelligence here."}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {data.results.map((r) => (
            <ResultCard
              key={`${r.brand}-${r.caliber}`}
              r={r}
              feePct={data.fee_pct}
              markupPct={data.markup_pct}
            />
          ))}
        </div>
      )}
    </div>
  );
}
